import { DialogContent, Stack, Typography } from "@mui/material";
import React, { Dispatch, useState } from "react";
import { ILoaded, IMutator } from "sonobello.utilities.react";

import StepperItem from "../../../../Components/CustomerStepper/StepperItem";
import Modal, { IModal } from "../../../../Components/Helpers/Modal";
import { CustomDimension } from "../../../../Models/CustomDimension";
import DispositionBlockingAlertValue from "../Types/DispositionErrorAlertValue";
import DispositionPicker from "./DispositionPicker";

/** The locally available properties available to the modal which confirms the lead result type selection. */
export interface IDispositionStepConfirmationModal
  extends Required<Pick<IModal, "open" | "handleClose" | "handleConfirm">> {
  /** The currently selected lead result type. */
  leadResultType: CustomDimension<number>;
}

export interface IDispositionStep extends ILoaded {
  /** Components which render an informational message to the user. */
  alerts: React.FC[];
  /** Components which render an informational message to the user and report a state that can block selection of a
   * disposition until resolved.*/
  blockingAlerts: React.FC<IMutator<DispositionBlockingAlertValue>>[];
  /** A flag indicating if the final disposition modal should be shown. */
  isModalOpen: boolean;
  /** The currently selected lead result type. */
  leadResultType: CustomDimension<number> | null;
  /** The lead result type options that should be presented to the user. */
  leadResultTypeOptions: CustomDimension<number>[];
  /** Flag indicating that the user should be shown a dialog which will allow them to confirm if they booked an appointment. */
  showBookingErrorConfirmation: boolean;
  /** Updates the flag for manual booking confirmation when the user designates it as such. */
  setIsBookingManuallyConfirmed: Dispatch<boolean>;
  /** Dispatch to set or clear the currently selected lead result type. */
  setLeadResultType: Dispatch<CustomDimension<number> | null>;
  /** The component which resolves the session's final details after selecting the lead result type. */
  ConfirmationModal: React.FC<IDispositionStepConfirmationModal>;
  /** The form component which updates the booking error status. */
  BookingErrorForm: React.FC<IMutator<boolean>>;
}

class ErrorAlertsStatus {
  readonly isErrored: boolean;
  private blockingAlertValues: DispositionBlockingAlertValue[];

  constructor(blockingAlertValues: DispositionBlockingAlertValue[]) {
    this.blockingAlertValues = blockingAlertValues;
    this.isErrored = blockingAlertValues.some(v => v.value);
  }

  update = (errorAlert: DispositionBlockingAlertValue): ErrorAlertsStatus => {
    const index = this.blockingAlertValues.findIndex(v => v.id === errorAlert.id);
    if (index !== -1) this.blockingAlertValues[index] = errorAlert;
    else this.blockingAlertValues.push(errorAlert);
    return new ErrorAlertsStatus([...this.blockingAlertValues]);
  };
}

/** The playbook step which allows the user to select the ultimate disposition of the session and be shown any detected
 * errors that are limiting the available options.
 */
const DispositionStep: React.FC<IDispositionStep> = ({
  alerts,
  blockingAlerts,
  isLoading,
  isModalOpen,
  leadResultType,
  leadResultTypeOptions,
  showBookingErrorConfirmation,
  setIsBookingManuallyConfirmed,
  setLeadResultType,
  ConfirmationModal,
  BookingErrorForm
}) => {
  const [errorAlertStatus, setErrorAlertStatus] = useState(new ErrorAlertsStatus([]));

  return (
    <>
      <StepperItem>
        <Stack spacing={2}>
          <Typography variant="body1" align="center">
            Completing this step will end your InContact connection with the guest.
          </Typography>
          {Boolean(alerts.length || blockingAlerts.length) && (
            <Stack spacing={1}>
              {alerts.map((Alert, index) => (
                <Alert key={index} />
              ))}
              {blockingAlerts.map((BlockingAlert, index) => (
                <BlockingAlert onChange={value => setErrorAlertStatus(status => status.update(value))} key={index} />
              ))}
            </Stack>
          )}
          <DispositionPicker
            options={leadResultTypeOptions}
            loadingOption={isLoading ? leadResultType : null}
            disabled={isLoading || errorAlertStatus.isErrored}
            onChange={setLeadResultType}
          />
        </Stack>
      </StepperItem>
      {leadResultType && (
        <ConfirmationModal
          leadResultType={leadResultType}
          open={isModalOpen}
          handleClose={() => setLeadResultType(null)}
          handleConfirm={() => setLeadResultType(null)}
        />
      )}
      <Modal title="Booking Confirmation" open={showBookingErrorConfirmation}>
        <DialogContent>
          <BookingErrorForm onChange={setIsBookingManuallyConfirmed} />
        </DialogContent>
      </Modal>
    </>
  );
};

export default DispositionStep;
